import { saveAndExit } from 'Pages/MessageEdit/recoil/actions/actions'
import {
  deleteAutosaveCallback,
  exitCallback,
  openModalSaveCallback,
  showPreviewCallback
} from 'Pages/MessageEdit/recoil/callbacks/callbacks'
import {
  beePluginState,
  editorState
} from 'Pages/MessageEdit/recoil/state/editor'
import {
  Button,
  ContextMenu,
  Flex,
  FlexItem,
  IconArrow1Down,
  makeButtonWithMenu
} from 'mailup-web-components'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { modalSaveCallback } from '../recoil/callbacks/callbacks'

import modalMessageRedirect from 'Helpers/modalMessageRedirect'
import { notifyParent } from 'Helpers/notifyParent'

import styled from '@emotion/styled'
import { modalContentsDefaultsState } from 'Components/ModalContentDefaults/recoil/state'
import {
  MESSAGE_STR_TYPE_CONFIRMATION,
  MESSAGE_STR_TYPE_WELCOME,
  STORE_KEY_ACCOUNT
} from 'Constants/index'
import { storeGet } from 'mailup-react-helpers'

const SaveMessageButton = () => {
  const intl = useIntl()
  const history = useHistory()
  const deleteAutosave = useRecoilCallback(deleteAutosaveCallback, [])

  const { mode } = useRecoilValue(editorState)

  const openModalSave = useRecoilCallback(openModalSaveCallback, [])
  const modalSave = useRecoilCallback(modalSaveCallback, [])
  const exit = useRecoilCallback(exitCallback, [])
  //  const saveAndExit = useRecoilCallback(saveAndExitCallback, [])
  const showPreview = useRecoilCallback(showPreviewCallback, [])
  const setModalContentsDefaults = useSetRecoilState(modalContentsDefaultsState)

  const openOverview = (id, messageType) => {
    const source =
      messageType === MESSAGE_STR_TYPE_WELCOME
        ? 'welcome'
        : messageType === MESSAGE_STR_TYPE_CONFIRMATION
        ? 'confirmation'
        : 'EmailMessages'
    const overviewRoute = `/messages/email/${id}/overview?source=${source}&op=upd`

    history.push(overviewRoute)
  }

  const {
    loaded: beePluginLoaded,
    instance: beePluginInstance
  } = useRecoilValue(beePluginState)

  const setEditorState = useSetRecoilState(editorState)
  const account = storeGet(STORE_KEY_ACCOUNT)

  const openQuickSend = ({ id }) => {
    notifyParent('quickSendMessage', [String(id)])
  }

  const { redirect } = modalMessageRedirect()

  const handleClickBtn = value => {
    switch (value) {
      case 'EXIT': {
        exit(true, messageType => {
          redirect(messageType)
        })

        break
      }

      case 'SAVE': {
        setEditorState(prev => ({
          ...prev,
          afterSaveCallback: undefined,
          action: value
        }))

        if (mode === 'edit') {
          beePluginInstance.save()
        } else {
          openModalSave()
        }

        break
      }

      case 'SAVE_AND_EXIT': {
        setEditorState(prev => ({
          ...prev,
          afterSaveCallback: ({ id, messageType }) => {
            saveAndExit({ id, messageType, onSuccess: openOverview })
          },
          action: value
        }))

        if (mode === 'edit') {
          beePluginInstance.save()
        } else {
          openModalSave()
        }

        break
      }
      case 'SAVE_AS_TEMPLATE': {
        modalSave()
        deleteAutosave()

        break
      }

      case 'QUICK_SEND': {
        setEditorState(prev => ({
          ...prev,
          afterSaveCallback: openQuickSend,
          action: value
        }))

        if (mode === 'edit') {
          beePluginInstance.save()
        } else {
          openModalSave('quickSend')
        }

        break
      }

      case 'PREVIEW': {
        setEditorState(prev => ({
          ...prev,
          afterSaveCallback: showPreview,
          action: value
        }))

        if (mode === 'edit') {
          beePluginInstance.save()
        } else {
          openModalSave('preview')
        }

        break
      }

      case 'STRUCTURE': {
        beePluginInstance.toggleStructure()
        break
      }

      case 'CONTENTDEFAULTS': {
        if (
          account.IdOffer === 3 ||
          account.IdOffer === 2 ||
          account.IdOffer === 1 ||
          account.IsOfferLight
        ) {
          setModalContentsDefaults(prev => ({
            ...prev,
            show: true
          }))
        } else {
          history.push(`/messages/email/contentdefaults`)
        }
        break
      }
    }
  }

  const ButtonComponent = () => (
    <Button
      kind='primary'
      data-mu-test='edit-message-menu'
      disabled={!beePluginLoaded}
    >
      <IconArrow1Down />
    </Button>
  )

  const MenuComponent = () => {
    const items = [
      {
        label: intl.formatMessage({ id: 'Load.Bee.SaveAsTemplate' }),
        value: 'SAVE_AS_TEMPLATE',
        onClick: handleClickBtn
      },
      {
        label: intl.formatMessage({ id: 'Load.Bee.SendTest' }),
        value: 'QUICK_SEND',
        onClick: handleClickBtn
      },
      {
        label: intl.formatMessage({ id: 'Load.Bee.Preview' }),
        value: 'PREVIEW',
        onClick: handleClickBtn
      },
      {
        label: intl.formatMessage({ id: 'Load.Bee.Structure' }),
        value: 'STRUCTURE',
        onClick: handleClickBtn
      },
      {
        separator: true,
        label: intl.formatMessage({ id: 'Load.NavMenu.EmailContentDefaults' }),
        value: 'CONTENTDEFAULTS',
        onClick: handleClickBtn
      }
    ]

    return <ContextMenu items={items} />
  }

  const ButtonWithMenu = makeButtonWithMenu(ButtonComponent, MenuComponent)

  return (
    <Flex columnGap='8px' justifyContent='flex-end'>
      <FlexItem>
        <Button
          kind='neutral'
          onClick={() => handleClickBtn('EXIT')}
          data-mu-test='save-message-exit'
        >
          {intl.formatMessage({ id: 'Load.Common.Exit' })}
        </Button>
      </FlexItem>
      <FlexItem>
        <Button
          kind='neutral'
          onClick={() => handleClickBtn('SAVE')}
          data-mu-test='save-message-save'
        >
          {intl.formatMessage({ id: 'Load.Common.Save' })}
        </Button>
      </FlexItem>

      <StyledSaveMessageContainer>
        <Flex alignItems='center'>
          <Button
            kind='primary'
            data-mu-test='save-message-save-and-exit'
            onClick={() => handleClickBtn('SAVE_AND_EXIT')}
            disabled={!beePluginLoaded}
          >
            {intl.formatMessage({ id: 'Load.Common.SaveAndExit' })}
          </Button>

          <ButtonWithMenu />
        </Flex>
      </StyledSaveMessageContainer>
    </Flex>
  )
}

export default SaveMessageButton

const StyledSaveMessageContainer = styled(FlexItem)`
  .e-button:first-of-type {
    border-radius: 4px 0 0 4px;
    &:hover {
      cursor: pointer;
    }
  }

  .c-button-with-menu {
    margin-right: 0;
  }

  .c-button-with-menu__button > .e-button {
    border-radius: 0 4px 4px 0;
    margin-left: 1px;
    padding: 0 13px;
  }

  .c-button-with-menu__menu {
    right: -8px;
  }
`
